import React, { useCallback, useEffect, useState } from 'react';
import { MdDirectionsBoat, MdClose, MdChevronRight } from 'react-icons/md';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '.';

// Grup Perjalanan di detail task: beberapa task (customer sama, 1 kapal per task) yg dikerjakan
// dalam 1 perjalanan. Dokumen perjalanan bersama & daftar bawaan checker berlaku utk semua task
// di grup. Yg bisa mengelola: pembuat task, assignee, checker, atau admin/approver (dicek backend → can_manage).
//
// useTripGroup: data + aksi + dialog (dirender sekali di level layar, dipicu dari menu kebab).
// TripGroupList: daftar anggota — hanya ditampilkan kalau task sudah tergabung di grup.

const BoatIcon = MdDirectionsBoat as any;
const CloseIcon = MdClose as any;
const ChevronIcon = MdChevronRight as any;

export interface TripGroupMember {
  id: number;
  description: string;
  status: string;
  vessel_id: number | null;
  vessel_name: string | null;
  checker_name: string | null;
  assignee_names: string | null;
  assignees: { id: number; name: string }[];
}

export interface TripGroupData {
  can_manage: boolean;
  client_id: number | null;
  group: { id: number; checker_name: string | null } | null;
  tasks: TripGroupMember[];
}

interface Candidate {
  id: number;
  description: string;
  status: string;
  vessel_name: string | null;
  checker_name: string | null;
  has_preparation: boolean;
}

type Pending =
  | { kind: 'checker'; taskId: number; groupChecker: string; taskChecker: string }
  | { kind: 'merge'; taskId: number; changeChecker: boolean; items: { title: string; group_qty: number; task_qty: number; total: number }[] };

const fill = (s: string, vars: Record<string, string>) => Object.entries(vars).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(v), s || '');

/**
 * @param taskId   task yg sedang dibuka
 * @param onChanged dipanggil setelah grup berubah (tambah/keluarkan) — layar memuat ulang dokumen & task
 */
export const useTripGroup = (taskId: number | undefined, onChanged?: () => void) => {
  const { t } = useLanguage();
  const tg = (t as any).tripGroup || {};
  const [data, setData] = useState<TripGroupData | null>(null);
  const [picker, setPicker] = useState<Candidate[] | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<TripGroupMember | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    if (!taskId) return;
    try {
      const r = await axiosInstance.get(`/task-groups/task/${taskId}`);
      setData(r.data);
    } catch { /* section grup cukup tidak tampil */ }
  }, [taskId]);

  useEffect(() => { setData(null); reload(); }, [reload]);

  const applyChange = (d: TripGroupData) => {
    setData(d);
    onChanged?.();
  };

  const openPicker = async () => {
    setError('');
    try {
      const r = await axiosInstance.get(`/task-groups/task/${taskId}/candidates`);
      setPicker(r.data || []);
    } catch (e: any) {
      setError(e.response?.data?.message || t.common.error);
      setPicker([]);
    }
  };

  const addMember = async (memberId: number, opts: { change_checker?: boolean; confirm_merge?: boolean } = {}) => {
    setBusy(true);
    setError('');
    try {
      const r = await axiosInstance.post(`/task-groups/task/${taskId}/members`, { task_id: memberId, ...opts });
      applyChange(r.data);
      setPicker(null);
      setPending(null);
    } catch (e: any) {
      const d = e.response?.data;
      if (d?.code === 'CHECKER_CONFLICT') {
        setPending({ kind: 'checker', taskId: memberId, groupChecker: d.group_checker_name || '-', taskChecker: d.task_checker_name || '-' });
      } else if (d?.code === 'PREP_MERGE_REQUIRED') {
        setPending({ kind: 'merge', taskId: memberId, changeChecker: !!opts.change_checker, items: d.items || [] });
      } else {
        setError(d?.message || t.common.error);
        setPending(null);
      }
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (member: TripGroupMember) => {
    setBusy(true);
    setError('');
    try {
      const r = await axiosInstance.delete(`/task-groups/task/${taskId}/members/${member.id}`);
      // Task ini sendiri yg keluar → muat ulang grup milik task ini (sekarang kosong)
      if (member.id === taskId) {
        const own = await axiosInstance.get(`/task-groups/task/${taskId}`);
        applyChange(own.data);
      } else {
        applyChange(r.data);
      }
      setConfirmRemove(null);
    } catch (e: any) {
      setError(e.response?.data?.message || t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const modalWrap = (children: React.ReactNode, onClose: () => void) => (
    <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#fff', width: '100%', maxWidth: 380, borderRadius: 16, padding: 20, maxHeight: '80vh', overflowY: 'auto' }}>
        {children}
      </div>
    </div>
  );

  const closePicker = () => { setPicker(null); setError(''); };

  const modals = (
    <>
      {/* Pemilih task: judul & tombol batal tetap di tempat, hanya daftar yg di-scroll */}
      {picker && (
        <div onClick={() => !busy && closePicker()} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#fff', width: '100%', maxWidth: 380, borderRadius: 16, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '20px 20px 12px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
              <text.H4>{tg.chooseTask || 'Pilih task dalam perjalanan yang sama'}</text.H4>
              {error && <text.T12 style={{ color: '#DC2626', display: 'block', marginTop: 8 }}>{error}</text.T12>}
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 20px' }}>
          {data && !data.client_id ? (
            <text.T14 style={{ color: '#6B7280', display: 'block', padding: '14px 0' }}>{tg.needClient}</text.T14>
          ) : picker.length === 0 ? (
            !error && <text.T14 style={{ color: '#6B7280', display: 'block', padding: '14px 0' }}>{tg.noCandidates}</text.T14>
          ) : picker.map((c) => (
            <div key={c.id} onClick={() => !busy && addMember(c.id)} style={{ padding: '10px 0', borderBottom: '1px solid #F3F4F6', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              <text.T14 style={{ fontWeight: '600', color: '#111827' }}>#{c.id} · {c.vessel_name || '-'}</text.T14>
              <text.T12 style={{ color: '#6B7280', display: 'block' }}>{c.status} · {c.description}</text.T12>
              <text.T12 style={{ color: '#9CA3AF', display: 'block' }}>
                {tg.checker || 'Checker'}: {c.checker_name || '-'}{c.has_preparation ? ` · ${tg.hasPreparation || 'Sudah ada persiapan'}` : ''}
              </text.T12>
            </div>
          ))}
            </div>
            <div style={{ padding: '12px 20px 20px', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
              <components.Button title={t.common.cancel} colorScheme="light" onClick={closePicker} />
            </div>
          </div>
        </div>
      )}

      {pending?.kind === 'checker' && modalWrap(
        <>
          <text.H4 style={{ marginBottom: 10 }}>{tg.checkerConflictTitle || 'Checker berbeda'}</text.H4>
          <text.T14 style={{ color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>
            {fill(tg.checkerConflictMsg, { task: pending.taskChecker, group: pending.groupChecker })}
          </text.T14>
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <components.Button title={t.common.cancel} colorScheme="light" containerStyle={{ flex: 1 }} onClick={() => setPending(null)} />
            <components.Button title={busy ? '...' : (tg.changeChecker || 'Ganti checker')} containerStyle={{ flex: 1 }}
              onClick={busy ? undefined : () => addMember(pending.taskId, { change_checker: true })} />
          </div>
        </>,
        () => setPending(null)
      )}

      {pending?.kind === 'merge' && modalWrap(
        <>
          <text.H4 style={{ marginBottom: 10 }}>{tg.mergeTitle || 'Gabungkan daftar bawaan'}</text.H4>
          <text.T14 style={{ color: theme.colors.bodyTextColor, lineHeight: 1.5, marginBottom: 10 }}>{tg.mergeMsg}</text.T14>
          {pending.items.map((i, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
              <text.T14 style={{ color: '#111827' }}>{i.title}</text.T14>
              <text.T14 style={{ color: '#059669', fontWeight: '600' }}>{i.group_qty} + {i.task_qty} = {i.total}</text.T14>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <components.Button title={t.common.cancel} colorScheme="light" containerStyle={{ flex: 1 }} onClick={() => setPending(null)} />
            <components.Button title={busy ? '...' : (tg.mergeConfirm || 'Gabungkan')} containerStyle={{ flex: 1 }}
              onClick={busy ? undefined : () => addMember(pending.taskId, { change_checker: pending.changeChecker, confirm_merge: true })} />
          </div>
        </>,
        () => setPending(null)
      )}

      {confirmRemove && modalWrap(
        <>
          <text.H4 style={{ marginBottom: 10 }}>{tg.remove || 'Keluarkan'}: {confirmRemove.vessel_name || `#${confirmRemove.id}`}</text.H4>
          <text.T14 style={{ color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>{tg.removeConfirm}</text.T14>
          {error && <text.T12 style={{ color: '#DC2626', display: 'block', marginTop: 8 }}>{error}</text.T12>}
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <components.Button title={t.common.cancel} colorScheme="light" containerStyle={{ flex: 1 }} onClick={() => { setConfirmRemove(null); setError(''); }} />
            <components.Button title={busy ? '...' : (tg.remove || 'Keluarkan')} containerStyle={{ flex: 1 }}
              onClick={busy ? undefined : () => removeMember(confirmRemove)} />
          </div>
        </>,
        () => { setConfirmRemove(null); setError(''); }
      )}
    </>
  );

  return {
    data,
    inGroup: !!data?.group,
    memberCount: data?.tasks?.length || 0,
    canManage: !!data?.can_manage,
    openPicker,
    requestRemove: (m: TripGroupMember) => { setError(''); setConfirmRemove(m); },
    modals,
  };
};

/** Daftar anggota grup — tiap baris bisa dibuka ke detail task-nya. */
export const TripGroupList: React.FC<{
  taskId: number;
  group: ReturnType<typeof useTripGroup>;
  onOpenTask: (member: TripGroupMember) => void;
}> = ({ taskId, group, onOpenTask }) => {
  const { t } = useLanguage();
  const tg = (t as any).tripGroup || {};
  const data = group.data;
  if (!data?.group) return null;

  return (
    <div>
      <text.H5 style={{ marginBottom: 4 }}>{tg.title || 'Grup Perjalanan'}</text.H5>
      <text.T12 style={{ color: '#6B7280', display: 'block', marginBottom: 10 }}>{tg.hint}</text.T12>
      <div style={{ border: '1px solid #E0F2FE', borderRadius: 12, overflow: 'hidden' }}>
        {data.tasks.map((m, idx) => {
          const isCurrent = m.id === taskId;
          return (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', backgroundColor: isCurrent ? '#F0F9FF' : '#fff', borderBottom: idx < data.tasks.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
              <div onClick={() => !isCurrent && onOpenTask(m)} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, cursor: isCurrent ? 'default' : 'pointer' }}>
                <BoatIcon size={18} color="#0284C7" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <text.T14 style={{ fontWeight: '600', color: '#111827' }}>
                    #{m.id} · {m.vessel_name || '-'}{isCurrent ? ` · ${tg.currentTask || 'Task ini'}` : ''}
                  </text.T14>
                  <text.T12 style={{ color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                    {m.status} · {m.description}
                  </text.T12>
                  {m.assignee_names && <text.T12 style={{ color: '#9CA3AF', display: 'block' }}>{m.assignee_names}</text.T12>}
                </div>
                {!isCurrent && <ChevronIcon size={18} color="#9CA3AF" />}
              </div>
              {group.canManage && (
                <div onClick={() => group.requestRemove(m)} style={{ cursor: 'pointer', padding: 4 }} title={tg.remove || 'Keluarkan'}>
                  <CloseIcon size={16} color="#EF4444" />
                </div>
              )}
            </div>
          );
        })}
        {data.group.checker_name && (
          <div style={{ padding: '6px 12px', backgroundColor: '#F9FAFB' }}>
            <text.T12 style={{ color: '#6B7280' }}>{tg.checker || 'Checker'}: {data.group.checker_name}</text.T12>
          </div>
        )}
      </div>
    </div>
  );
};
